'use client';

import NextLink from 'next/link';
import { Avatar, Dropdown, Label } from '@heroui/react';
import { signOut, useSession } from 'next-auth/react';
import { useEffect, useState } from 'react';
import { getAvatarUrl } from '@/lib/avatarUtils';
import { Icon } from '@iconify/react';
import NotificationBell from '@/components/notifications/NotificationBell';
import { ThemeSwitch } from '@/components/theme-switch';
import { Logo } from '@/components/icons';
import { useTenantContext } from '@/lib/useTenantContext';

export const LoggedInNavbar = () => {
  const { data: session, status } = useSession();
  const [isMounted, setIsMounted] = useState(false);
  const { context: tenantContext } = useTenantContext();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleLogout = async () => {
    try {
      // Use /next-api/ prefix to avoid Azure Front Door routing to backend
      await fetch('/next-api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch (error) {
      console.error('Failed to revoke refresh token on logout:', error);
    } finally {
      const absoluteCallbackUrl = `${window.location.origin}/login`;
      await signOut({ callbackUrl: absoluteCallbackUrl });
    }
  };

  if (!isMounted || status === 'loading') return null;

  const userName = session?.user?.name ?? '';
  const isAdmin = session?.user?.isAdmin ?? false;
  const userId = session?.user?.id?.toString();
  const avatarUrl = getAvatarUrl(userId);
  // Keep nav distinctive color for demo/trial regardless of state (matches /welcome header, which stays colored even when expired/suspended)
  const isDemo = tenantContext?.tier === 'demo';
  const isTrial = tenantContext?.tier === 'trial';

  return (
    <nav className="sticky top-0 z-40 w-full bg-background/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2">
        <div className="flex items-center gap-4">
          <NextLink href="/" className="flex items-center gap-2">
            <Logo size={24} />
            <span className="font-bold">ConvoPro</span>
          </NextLink>
          {isDemo ? (
            <NextLink
              href="/welcome"
              className="rounded-md px-2 py-1.5 text-sm font-medium text-orange-600 hover:bg-orange-50 transition-colors flex items-center gap-1.5"
            >
              <Icon icon="solar:star-shine-bold" width={14} />
              Demo Guide
            </NextLink>
          ) : isTrial ? (
            <NextLink
              href="/welcome"
              className="rounded-md px-2 py-1.5 text-sm font-medium text-accent hover:bg-accent/10 transition-colors flex items-center gap-1.5"
            >
              <Icon icon="solar:rocket-bold" width={14} />
              Trial Guide
            </NextLink>
          ) : (
            <NextLink
              href="/welcome"
              className="rounded-md px-2 py-1.5 text-sm font-medium text-foreground hover:bg-muted-100 transition-colors"
            >
              Welcome
            </NextLink>
          )}
          <NextLink
            href="/ai-chat"
            className="rounded-md px-2 py-1.5 text-sm font-medium text-foreground hover:bg-muted-100 transition-colors"
          >
            AI Chat
          </NextLink>
        </div>
        <div className="flex items-center gap-0.5">
          <NotificationBell buttonSize="lg" />
          <ThemeSwitch />
          <Dropdown>
            <Dropdown.Trigger>
              <div className="flex cursor-pointer items-center gap-2 rounded-md p-1 hover:bg-muted-100 transition-colors">
                <Avatar size="sm">
                  <Avatar.Image src={avatarUrl} alt={userName} />
                  <Avatar.Fallback>{userName?.charAt(0).toUpperCase()}</Avatar.Fallback>
                </Avatar>
                <span className="hidden text-sm font-medium sm:block">{userName}</span>
              </div>
            </Dropdown.Trigger>
            <Dropdown.Popover>
              <Dropdown.Menu onAction={(key) => {
                if (key === 'logout') handleLogout();
              }}>
                {isAdmin ? (
                  <Dropdown.Item id="dashboard" textValue="Dashboard" href="/admin/dashboard">
                    <Icon icon="solar:widget-linear" width={20} />
                    <Label>Dashboard</Label>
                  </Dropdown.Item>
                ) : null}
                <Dropdown.Item id="profile" textValue="Profile" href="/profile">
                  <Icon icon="solar:user-id-linear" width={20} />
                  <Label>Profile</Label>
                </Dropdown.Item>
                <Dropdown.Item id="connections" textValue="My Connections" href="/credentials">
                  <Icon icon="solar:link-circle-linear" width={20} />
                  <Label>My Connections</Label>
                </Dropdown.Item>
                <Dropdown.Item id="logout" textValue="Logout" variant="danger">
                  <Icon icon="solar:minus-circle-line-duotone" width={20} />
                  <Label>Logout</Label>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>
        </div>
      </div>
    </nav>
  );
};
